/**
 * AdminSpotlightTab — Manage the "Businesses of the Week" curated roster.
 *
 * Features:
 *   - Search any business by name → click Add to append
 *   - Drag-and-drop the current roster to reorder (HTML5 DnD, no extra deps)
 *   - Remove entries with a Trash icon
 *   - Trigger a manual Sunday Spotlight send (dry-run by default)
 *
 * Backed by `/api/admin/curated/roster` (GET / PUT / POST add / DELETE).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Trophy, GripVertical, X, Search as SearchIcon, Plus, Save, Send,
  Sparkles, RotateCcw, Loader2, ChevronUp, ChevronDown, ArrowUpToLine, ArrowDownToLine,
} from "lucide-react";
import BusinessLogo from "@/components/BusinessLogo";

export default function AdminSpotlightTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roster, setRoster] = useState([]);          // ordered array of business docs
  const [isFallback, setIsFallback] = useState(false);

  const [query, setQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [results, setResults] = useState([]);       // search results

  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const dirty = useRef(false);

  // ---- Load current roster ----------------------------------------
  const loadRoster = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/curated/roster`);
      setRoster(data.businesses || []);
      setIsFallback(!!data.is_fallback);
      dirty.current = false;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load roster");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRoster(); }, [loadRoster]);

  // ---- Search (debounced) -----------------------------------------
  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const controller = new AbortController();
    const t = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const { data } = await axios.get(`${API}/businesses`, {
          params: { search: query.trim(), limit: 20 },
          signal: controller.signal,
        });
        setResults(data.businesses || []);
      } catch (e) {
        if (!axios.isCancel(e) && e.name !== "CanceledError") {
          // silent
        }
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => { controller.abort(); clearTimeout(t); };
  }, [query]);

  // Remove already-in-roster from results
  const rosterIds = useMemo(() => new Set(roster.map((b) => b.business_id)), [roster]);
  const filteredResults = useMemo(
    () => results.filter((b) => !rosterIds.has(b.business_id)),
    [results, rosterIds],
  );

  // ---- Mutations ---------------------------------------------------
  const addToRoster = async (biz) => {
    try {
      await axios.post(`${API}/admin/curated/roster/add`, { business_id: biz.business_id });
      toast.success(`${biz.name} added to the roster`);
      await loadRoster();
      setQuery("");
      setResults([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    }
  };

  const removeFromRoster = async (biz) => {
    try {
      await axios.delete(`${API}/admin/curated/roster/${biz.business_id}`);
      toast.success(`${biz.name} removed`);
      await loadRoster();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    }
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      const ids = roster.map((b) => b.business_id);
      await axios.put(`${API}/admin/curated/roster`, { business_ids: ids });
      toast.success("New order saved — live on the site instantly.");
      dirty.current = false;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save order");
    } finally {
      setSaving(false);
    }
  };

  const seedFallback = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/curated/roster/seed-fallback`);
      toast.success("Roster seeded from fallback list");
      await loadRoster();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to seed");
    } finally {
      setSaving(false);
    }
  };

  const runSpotlightEmail = async (dryRun) => {
    try {
      const { data } = await axios.post(`${API}/spotlight-email/run-now?dry_run=${dryRun}`);
      const verb = dryRun ? "Dry-run" : "Sent";
      toast.success(`${verb}: ${data.sent} email(s) · skipped ${data.skipped}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  // ---- Drag-and-drop handlers -------------------------------------
  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (e, i) => { e.preventDefault(); setOverIdx(i); };
  const onDrop = (e, i) => {
    e.preventDefault();
    if (dragIdx == null || dragIdx === i) { setDragIdx(null); setOverIdx(null); return; }
    setRoster((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(i, 0, moved);
      return next;
    });
    dirty.current = true;
    setDragIdx(null);
    setOverIdx(null);
  };
  const onDragEnd = () => { setDragIdx(null); setOverIdx(null); };

  // ---- Button-based reorder (touch-friendly) ----------------------
  const moveTo = (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= roster.length || fromIdx === toIdx) return;
    setRoster((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
    dirty.current = true;
  };
  const moveUp = (i) => moveTo(i, i - 1);
  const moveDown = (i) => moveTo(i, i + 1);
  const moveToTop = (i) => moveTo(i, 0);
  const moveToBottom = (i) => moveTo(i, roster.length - 1);

  // ---- Render ------------------------------------------------------
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="spotlight-loading">
        <Loader2 className="w-6 h-6 animate-spin text-tbc-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-spotlight-tab">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl text-tbc-navy flex items-center gap-2">
            <Trophy className="w-5 h-5 text-tbc-gold" />
            Businesses of the Week
          </h2>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Use the arrow buttons to reorder (works on phone + desktop), or drag
            the grip handle on desktop. Click <strong>X</strong> to remove, or
            search below to add. Changes go live after you click{" "}
            <strong>Save order</strong>.
          </p>
          {isFallback && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5 mt-3 inline-flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" />
              Showing the fallback list from code. Save an order to persist it in the DB.
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={seedFallback}
            disabled={saving}
            data-testid="spotlight-seed-fallback-btn"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
            Seed from fallback
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => runSpotlightEmail(true)}
            data-testid="spotlight-dryrun-btn"
          >
            <Send className="w-3.5 h-3.5 mr-1.5" />
            Test email (dry-run)
          </Button>
          <Button
            size="sm"
            onClick={() => {
              if (window.confirm("Send the Spotlight email to every member right now?")) {
                runSpotlightEmail(false);
              }
            }}
            className="bg-tbc-navy hover:bg-tbc-navy/90 text-white"
            data-testid="spotlight-send-now-btn"
          >
            <Send className="w-3.5 h-3.5 mr-1.5" />
            Send now
          </Button>
        </div>
      </div>

      {/* Current roster */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
          <p className="text-sm text-gray-600">
            <strong>{roster.length}</strong> business{roster.length === 1 ? "" : "es"} in rotation
          </p>
          <Button
            size="sm"
            onClick={saveOrder}
            disabled={saving}
            className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy"
            data-testid="spotlight-save-order-btn"
          >
            <Save className="w-3.5 h-3.5 mr-1.5" />
            {saving ? "Saving…" : "Save order"}
          </Button>
        </div>

        {roster.length === 0 ? (
          <div className="text-center py-12 text-gray-500 text-sm">
            No businesses in the rotation yet. Search below to add some.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100" data-testid="spotlight-roster-list">
            {roster.map((b, i) => (
              <li
                key={b.business_id}
                draggable
                onDragStart={() => onDragStart(i)}
                onDragOver={(e) => onDragOver(e, i)}
                onDrop={(e) => onDrop(e, i)}
                onDragEnd={onDragEnd}
                className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                  overIdx === i && dragIdx !== i ? "bg-tbc-gold/10" : ""
                } ${dragIdx === i ? "opacity-40" : "hover:bg-gray-50"}`}
                data-testid={`spotlight-roster-item-${b.business_id}`}
              >
                <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0 cursor-grab active:cursor-grabbing hidden sm:inline-block" />
                <span className="text-xs text-gray-400 font-mono w-6 flex-shrink-0">
                  #{i + 1}
                </span>
                <BusinessLogo name={b.name} logoUrl={b.logo_url} size="sm" shape="rounded" />
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-sm text-tbc-navy truncate">{b.name}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {b.category}
                    {b.city && b.state ? ` · ${b.city}, ${b.state}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => moveToTop(i)}
                    disabled={i === 0}
                    className="p-1.5 text-gray-500 hover:text-tbc-navy hover:bg-tbc-navy/5 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label={`Move ${b.name} to top`}
                    title="Move to top"
                    data-testid={`spotlight-move-top-${b.business_id}`}
                  >
                    <ArrowUpToLine className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveUp(i)}
                    disabled={i === 0}
                    className="p-1.5 text-gray-500 hover:text-tbc-navy hover:bg-tbc-navy/5 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label={`Move ${b.name} up`}
                    title="Move up"
                    data-testid={`spotlight-move-up-${b.business_id}`}
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveDown(i)}
                    disabled={i === roster.length - 1}
                    className="p-1.5 text-gray-500 hover:text-tbc-navy hover:bg-tbc-navy/5 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label={`Move ${b.name} down`}
                    title="Move down"
                    data-testid={`spotlight-move-down-${b.business_id}`}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveToBottom(i)}
                    disabled={i === roster.length - 1}
                    className="p-1.5 text-gray-500 hover:text-tbc-navy hover:bg-tbc-navy/5 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label={`Move ${b.name} to bottom`}
                    title="Move to bottom"
                    data-testid={`spotlight-move-bottom-${b.business_id}`}
                  >
                    <ArrowDownToLine className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeFromRoster(b)}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors ml-1"
                    aria-label={`Remove ${b.name}`}
                    data-testid={`spotlight-remove-${b.business_id}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Add businesses */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <p className="font-heading text-sm text-tbc-navy mb-3">
          Add a business to the rotation
        </p>
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by business name…"
            className="pl-10"
            data-testid="spotlight-search-input"
          />
          {searchLoading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
          )}
        </div>
        {filteredResults.length > 0 && (
          <ul className="mt-3 border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-72 overflow-y-auto" data-testid="spotlight-search-results">
            {filteredResults.map((b) => (
              <li key={b.business_id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50">
                <BusinessLogo name={b.name} logoUrl={b.logo_url} size="sm" shape="rounded" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-tbc-navy truncate">{b.name}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {b.category}
                    {b.city && b.state ? ` · ${b.city}, ${b.state}` : ""}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => addToRoster(b)}
                  data-testid={`spotlight-add-${b.business_id}`}
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Add
                </Button>
              </li>
            ))}
          </ul>
        )}
        {query && !searchLoading && filteredResults.length === 0 && results.length === 0 && (
          <p className="text-xs text-gray-500 mt-2">No businesses match “{query}”.</p>
        )}
        {query && !searchLoading && filteredResults.length === 0 && results.length > 0 && (
          <p className="text-xs text-gray-500 mt-2">All matches are already in the rotation.</p>
        )}
      </div>
    </div>
  );
}
